import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { findCountryOption, toCountryOption } from "../../utils/countryOptions";
import styles from "./CountryPicker.module.css";

const MENU_WIDTH = 280;
const MENU_MAX_HEIGHT = 300;
const MENU_MIN_HEIGHT = 160;

const normalize = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

function CountryFlag({ option, className }) {
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [option?.flagSrc]);

  if (!option?.flagSrc || broken) {
    return <span className={`${styles.flag} ${styles.flagPlaceholder} ${className || ""}`} aria-hidden="true" />;
  }

  return (
    <img
      src={option.flagSrc}
      alt=""
      className={`${styles.flag} ${className || ""}`}
      onError={() => setBroken(true)}
    />
  );
}

const VARIANT_CLASSES = {
  inline: "triggerInline",
  compact: "triggerCompact",
  field: "triggerField",
};

export default function CountryPicker({
  options = [],
  value = null,
  onChange,
  placeholder = "País",
  disabled = false,
  variant = "inline",
  display = "name",
  clearable = false,
  ariaLabel = "País",
  ariaInvalid = false,
  ariaRequired = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuPosition, setMenuPosition] = useState(null);
  const listboxId = useId();
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const selected = useMemo(() => {
    const match = findCountryOption(options, value);
    if (match) return match;
    if (value && typeof value === "object" && (value.name || value.description)) {
      return toCountryOption(value);
    }
    return null;
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    const query = normalize(search);
    if (!query) return options;

    return options.filter((option) =>
      [option.name, option.description, option.code, option.dial]
        .filter(Boolean)
        .some((field) => normalize(field).includes(query)),
    );
  }, [options, search]);

  const updateMenuPosition = useCallback(() => {
    const anchorRect = rootRef.current?.getBoundingClientRect();
    if (!anchorRect) return;

    const margin = 8;
    const gap = 4;
    const width = Math.max(MENU_WIDTH, variant === "inline" ? anchorRect.width : 0);
    const left = Math.max(margin, Math.min(anchorRect.left, window.innerWidth - width - margin));
    const spaceBelow = window.innerHeight - anchorRect.bottom - gap - margin;
    const maxHeight = Math.min(MENU_MAX_HEIGHT, Math.max(spaceBelow, MENU_MIN_HEIGHT));

    setMenuPosition({
      left,
      width,
      top: Math.min(anchorRect.bottom + gap, window.innerHeight - margin - maxHeight),
      maxHeight,
    });
  }, [variant]);

  useEffect(() => {
    if (!open) {
      setMenuPosition(null);
      setSearch("");
      return undefined;
    }

    updateMenuPosition();
    queueMicrotask(() => searchRef.current?.focus());

    const handleMouseDown = (event) => {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleScroll = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleResize = () => setOpen(false);

    document.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, updateMenuPosition]);

  const selectOption = (option) => {
    onChange?.(option);
    setOpen(false);
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      return;
    }

    if (event.key === "Enter" && filteredOptions.length > 0 && event.target === searchRef.current) {
      event.preventDefault();
      selectOption(filteredOptions[0]);
      return;
    }

    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

    const items = Array.from(menuRef.current?.querySelectorAll('[role="option"]') ?? []);
    if (!items.length) return;

    event.preventDefault();
    const currentIndex = items.indexOf(document.activeElement);
    const nextIndex =
      event.key === "ArrowDown"
        ? Math.min(currentIndex + 1, items.length - 1)
        : Math.max(currentIndex - 1, 0);
    items[nextIndex].focus();
  };

  const triggerLabel =
    display === "dial" ? selected?.dial || selected?.code || "" : selected?.name || "";

  const menu =
    open && menuPosition
      ? createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            style={menuPosition}
            onKeyDown={handleMenuKeyDown}
          >
            <div className={styles.searchWrapper}>
              <input
                ref={searchRef}
                type="text"
                className={styles.searchInput}
                placeholder="Buscar país..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label="Buscar país"
              />
            </div>

            <div id={listboxId} className={styles.list} role="listbox" aria-label={ariaLabel}>
              {filteredOptions.length === 0 ? (
                <div className={styles.empty}>Sin resultados</div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected =
                    selected != null &&
                    (option.id != null ? option.id === selected.id : option.code === selected.code);

                  return (
                    <button
                      key={option.id ?? option.code}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={isSelected ? `${styles.option} ${styles.optionSelected}` : styles.option}
                      onClick={() => selectOption(option)}
                    >
                      <CountryFlag option={option} />
                      <span className={styles.optionName}>{option.name}</span>
                      {option.dial ? <span className={styles.optionDial}>{option.dial}</span> : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  const rootClassName = `${styles.root} ${variant === "compact" ? styles.rootCompact : ""}`;
  const triggerClassName = [
    styles.trigger,
    styles[VARIANT_CLASSES[variant] ?? VARIANT_CLASSES.inline],
    open ? styles.triggerOpen : "",
    disabled ? styles.triggerDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={rootRef} className={rootClassName}>
      <button
        type="button"
        role="combobox"
        className={triggerClassName}
        onClick={() => {
          if (!disabled) setOpen((previous) => !previous);
        }}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
      >
        <span className={styles.triggerContent}>
          {selected ? <CountryFlag option={selected} /> : null}
          <span className={triggerLabel ? styles.triggerLabel : styles.triggerPlaceholder}>
            {triggerLabel || placeholder}
          </span>
        </span>
        <span className={styles.chevron} aria-hidden="true">
          ▾
        </span>
      </button>

      {clearable && selected && !disabled ? (
        <button
          type="button"
          className={styles.clearButton}
          onClick={(event) => {
            event.stopPropagation();
            onChange?.(null);
            setOpen(false);
          }}
          aria-label="Quitar país"
        >
          ×
        </button>
      ) : null}

      {menu}
    </div>
  );
}
